import React, { useState, useRef, useEffect } from "react";

const typeaheadStyles = `
  .entity-typeahead__wrapper {
    position: relative;
    display: flex;
  }
  .entity-typeahead__selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .entity-typeahead__selected-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .entity-typeahead__clear {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 15px;
    line-height: 1;
    padding: 0 0 0 8px;
  }
  .entity-typeahead__clear:hover {
    color: #111827;
  }
  .entity-typeahead__results {
    position: absolute;
    top: 36px;
    left: 0;
    right: 0;
    z-index: 20;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    max-height: 220px;
    overflow-y: auto;
  }
  .entity-typeahead__result {
    padding: 8px 10px;
    font-size: 13px;
    color: #111827;
    cursor: pointer;
  }
  .entity-typeahead__result:hover {
    background: #f3f4f6;
  }
  .entity-typeahead__status {
    padding: 8px 10px;
    font-size: 13px;
    color: #6b7280;
  }
`;

export default function EntityTypeahead({
  label,
  placeholder,
  value,
  onSelect,
  onClear,
  search,
  formatOption
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  function handleInputChange(e) {
    const nextTerm = e.target.value;
    setTerm(nextTerm);
    clearTimeout(debounceRef.current);

    if (nextTerm.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setShowResults(false);
      return;
    }

    setShowResults(true);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const matches = await search(nextTerm.trim());
      setResults(matches || []);
      setSearching(false);
    }, 300);
  }

  function handleSelect(entity) {
    setTerm("");
    setResults([]);
    setShowResults(false);
    onSelect(entity);
  }

  function handleClear() {
    setTerm("");
    setResults([]);
    setShowResults(false);
    onClear();
  }

  return (
    <div className="filter-bar__field">
      <span className="filter-bar__label">{label}</span>
      <div className="entity-typeahead__wrapper">
        {value ? (
          <div className="filter-bar__input entity-typeahead__selected">
            <span className="entity-typeahead__selected-text">{formatOption(value)}</span>
            <button
              type="button"
              className="entity-typeahead__clear"
              onClick={handleClear}
              aria-label="Clear"
            >
              &times;
            </button>
          </div>
        ) : (
          <input
            className="filter-bar__input"
            placeholder={placeholder}
            value={term}
            onChange={handleInputChange}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            onFocus={() => term.trim().length >= 2 && setShowResults(true)}
          />
        )}
        {showResults && !value && (
          <div className="entity-typeahead__results">
            {searching && <div className="entity-typeahead__status">Searching...</div>}
            {!searching && results.length === 0 && (
              <div className="entity-typeahead__status">No matches</div>
            )}
            {!searching &&
              results.map(entity => (
                <div
                  key={entity.uuid}
                  className="entity-typeahead__result"
                  onMouseDown={() => handleSelect(entity)}
                >
                  {formatOption(entity)}
                </div>
              ))}
          </div>
        )}
      </div>
      <style>{typeaheadStyles}</style>
    </div>
  );
}
